import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import { and, eq, not } from 'ember-truth-helpers';
import { formatSuccessRate } from 'garaje/components/health-dashboard/format-success-rate';
import UiCard from 'garaje/components/ui/card';
import LoadingSpinnerPulse from 'garaje/components/ui/classic/loading-spinner-pulse';
import restartAnimation from 'garaje/modifiers/restart-animation';

const CHART_LEFT_MARGIN = 55;

const CHART_COLORS = [
  '#7083ff',
  '#ff8f33',
  '#4ec176',
  '#b8be04',
  '#fa4338',
  '#4141a2',
  '#f76902',
  '#717684',
  '#018940',
  '#c25700',
  '#8e9d01',
  '#0ea5e9',
  '#e040fb',
  '#00bcd4',
  '#ff5252',
  '#69f0ae',
  '#ffeb3b',
  '#40c4ff',
  '#ff6d00',
  '#b388ff',
  '#f06292',
  '#80cbc4',
  '#aed581',
  '#ffcc02',
  '#4dd0e1',
  '#ff8a65',
  '#9575cd',
  '#a5d6a7',
  '#ef9a9a',
  '#80deea',
  '#ce93d8',
  '#ffab40',
  '#26a69a',
  '#ec407a',
  '#5c6bc0',
  '#d4e157',
  '#26c6da',
  '#ffa726',
  '#ab47bc',
  '#66bb6a',
];

const BUCKET_SIZE_MS: Record<string, number> = {
  'Last 1 hour': 5 * 60 * 1000,
  'Last 24 hours': 60 * 60 * 1000,
  'Last 7 days': 8 * 60 * 60 * 1000,
  'Last 30 days': 24 * 60 * 60 * 1000,
};

interface ChartDataPoint {
  startMs: number;
  isEmpty?: boolean;
  successRate?: number;
  installCounts?: Record<string, number>;
  installSuccessCounts?: Record<string, number>;
  typeCounts?: Record<string, number>;
  installTypeCounts?: Record<string, Record<string, number>>;
}

interface TimelineData {
  chartData?: ChartDataPoint[];
  meta?: { total: number };
  summary?: {
    lastSeenAt?: number;
    operationsSent?: number;
    operationsReceived?: number;
    accessEventsReceived?: number;
  };
}

interface InstallOption {
  label: string;
  value: string;
}

interface HealthDashboardChartsSignature {
  Args: {
    timelineData: TimelineData | null;
    selectedTimeRange: string;
    installOptions: InstallOption[];
    isLoading: boolean;
    isChartProgressing: boolean;
    chartProgressLoaded: number;
    chartProgressTotal: number;
    chartLoadKey: number;
  };
}

interface ChartLine {
  installId: string;
  color: string;
  pathD: string;
  points: Array<{ x: number; y: number; rate: number; bucketIdx: number }>;
}

interface ChartBucket {
  x: number;
  startMs: number;
  isEmpty: boolean;
  installCounts: Record<string, number>;
  successRate: number | null;
}

interface ChartGridLine {
  y: number;
  labelY: number;
  label: string;
  labelX: number;
}

interface ChartXLabel {
  x: number;
  label: string;
  textAnchor: string;
}

interface ChartLegendItem {
  installId: string;
  label: string;
  color: string;
  isHidden: boolean;
  rate: string | null;
  volume: number;
}

interface ChartComputed {
  lines: ChartLine[];
  buckets: ChartBucket[];
  xLabels: ChartXLabel[];
  gridLines: ChartGridLine[];
  legend: ChartLegendItem[];
}

interface EventTypeChartSegment {
  installId: string;
  count: number;
  y: number;
  height: number;
  color: string;
  pathD: string;
}

interface EventTypeChartBar {
  x: number;
  barX: number;
  barWidth: number;
  total: number;
  topY: number;
  segments: EventTypeChartSegment[];
  startMs: number;
  isEmpty: boolean;
  typeCounts: Record<string, number>;
  installCounts: Record<string, number>;
  installTypeCounts: Record<string, Record<string, number>>;
}

interface EventTypeChartLegendItem {
  installId: string;
  label: string;
  color: string;
  isHidden: boolean;
  volume: string;
  tooltipText: string;
}

interface EventTypeChartComputed {
  bars: EventTypeChartBar[];
  gridLines: ChartGridLine[];
  legend: EventTypeChartLegendItem[];
}

interface ChartHoveredPoint {
  x: number;
  tooltipStyle: ReturnType<typeof htmlSafe>;
  tooltipBelow: boolean;
  dateValue: string;
  successRateDisplay: string;
  installRows: Array<{ dotStyle: ReturnType<typeof htmlSafe>; label: string; rate: string; rawRate: number }>;
}

interface EventTypeChartHoveredPoint {
  x: number;
  tooltipStyle: ReturnType<typeof htmlSafe>;
  dateValue: string;
  total: number;
  typeRows: Array<{ label: string; count: number }>;
  installLabel: string | null;
  installDotStyle: ReturnType<typeof htmlSafe> | null;
}

export default class HealthDashboardCharts extends Component<HealthDashboardChartsSignature> {
  @tracked _hiddenInstalls: string[] = [];
  @tracked _highlightedInstallId: string | null = null;
  @tracked _highlightedInstallIds: string[] = [];
  @tracked _hoveredDotIndex: number | null = null;
  @tracked _hoveredEventTypeBarIndex: number | null = null;
  @tracked _hoveredSegmentInstallId: string | null = null;
  @tracked _chartSvgPixelWidth: number | null = null;
  @tracked _eventTypeChartSvgPixelWidth: number | null = null;

  get selectedTimeRangeLabel(): string {
    return this.args.selectedTimeRange.replace(/^Last\s+/i, 'the past ');
  }

  get _rawChartData(): ChartDataPoint[] {
    return this.args.timelineData?.chartData ?? [];
  }

  get _chartComputed(): ChartComputed | null {
    const points = this._rawChartData;
    if (!points.length) return null;

    const nonEmptyPoints = points.filter((p) => !p.isEmpty);
    if (!nonEmptyPoints.length) return null;

    const installTotals: Record<string, number> = {};
    for (const p of points) {
      for (const [id, count] of Object.entries(p.installCounts ?? {})) {
        installTotals[id] = (installTotals[id] ?? 0) + count;
      }
    }
    const allInstallIds = Object.keys(installTotals).sort((a, b) => (installTotals[b] ?? 0) - (installTotals[a] ?? 0));
    const installColorMap: Record<string, string> = {};
    allInstallIds.forEach((id, idx) => {
      installColorMap[id] = CHART_COLORS[idx % CHART_COLORS.length] ?? '#7083ff';
    });

    const installLabelMap: Record<string, string> = {};
    for (const opt of this.args.installOptions) {
      if (opt.value) installLabelMap[opt.value] = opt.label;
    }

    const hidden = new Set(this._hiddenInstalls);
    const visibleInstallIds = allInstallIds.filter((id) => !hidden.has(id));

    const perInstallRate = (p: ChartDataPoint, installId: string): number | null => {
      const counts = p.installCounts ?? {};
      const successCounts = p.installSuccessCounts ?? {};
      const total = counts[installId] ?? 0;
      const successes = successCounts[installId] ?? 0;
      if (!total) return null;
      return (successes * 100.0) / total;
    };

    const recomputeRate = (p: ChartDataPoint): number | null => {
      if (!p.installSuccessCounts || !Object.keys(p.installSuccessCounts).length) return p.successRate ?? null;
      const counts = p.installCounts ?? {};
      const successCounts = p.installSuccessCounts;
      const total = visibleInstallIds.reduce((s, id) => s + (counts[id] ?? 0), 0);
      const successes = visibleInstallIds.reduce((s, id) => s + (successCounts[id] ?? 0), 0);
      if (!total) return null;
      return (successes * 100.0) / total;
    };

    const allRates: number[] = [];
    for (const p of nonEmptyPoints) {
      for (const id of visibleInstallIds) {
        const rate = perInstallRate(p, id);
        if (rate !== null) allRates.push(rate);
      }
    }
    if (!allRates.length) allRates.push(100);

    const minRate = Math.min(...allRates);
    const maxRate = Math.max(...allRates);
    const range = maxRate - minRate;
    let buffer: number, step: number, useDecimals: boolean;
    if (range < 0.1) {
      buffer = 0.2;
      step = 0.5;
      useDecimals = true;
    } else if (range < 0.5) {
      buffer = 0.3;
      step = 0.5;
      useDecimals = true;
    } else if (range < 2) {
      buffer = 0.5;
      step = 1;
      useDecimals = false;
    } else if (range < 5) {
      buffer = 1;
      step = 2;
      useDecimals = false;
    } else if (range < 10) {
      buffer = 2;
      step = 5;
      useDecimals = false;
    } else {
      buffer = 5;
      step = 10;
      useDecimals = false;
    }
    const yMin = Math.max(0, Math.floor((minRate - buffer) / step) * step);
    const yMax = Math.min(100, Math.ceil((maxRate + buffer) / step) * step);
    const yRange = yMax - yMin || 1;
    const yScale = (rate: number) => Math.round(90 - ((rate - yMin) / yRange) * 80);

    const singlePoint = points.length === 1;
    const slotW = (624 - CHART_LEFT_MARGIN) / Math.max(points.length - 1, 1);

    const buckets = points.map((p, i) => {
      const x = singlePoint ? Math.round((624 + CHART_LEFT_MARGIN) / 2) : Math.round(i * slotW) + CHART_LEFT_MARGIN;
      return {
        x,
        startMs: p.startMs,
        isEmpty: p.isEmpty ?? false,
        installCounts: p.installCounts ?? {},
        successRate: p.isEmpty ? null : recomputeRate(p),
      };
    });

    const lines = visibleInstallIds.map((id) => {
      const linePoints: { x: number; y: number; rate: number; bucketIdx: number }[] = [];
      for (let i = 0; i < points.length; i++) {
        const rate = perInstallRate(points[i]!, id);
        if (rate !== null) {
          linePoints.push({ x: buckets[i]!.x, y: yScale(rate), rate, bucketIdx: i });
        }
      }
      let pathD = '';
      if (linePoints.length > 0) {
        pathD = `M${linePoints[0]!.x},${linePoints[0]!.y}`;
        for (let j = 1; j < linePoints.length; j++) {
          pathD += ` L${linePoints[j]!.x},${linePoints[j]!.y}`;
        }
      }
      return { installId: id, color: installColorMap[id] ?? '#7083ff', pathD, points: linePoints };
    });

    const timeRange = this.args.selectedTimeRange;
    let xLabels: { x: number; label: string; textAnchor: string }[];
    if (singlePoint) {
      const isTimeLabel = timeRange === 'Last 1 hour' || timeRange === 'Last 24 hours';
      const lbl = isTimeLabel
        ? new Date(buckets[0]!.startMs).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
        : new Date(buckets[0]!.startMs).toLocaleString('en-US', { month: 'short', day: 'numeric' });
      xLabels = [{ x: buckets[0]!.x, label: lbl, textAnchor: 'middle' }];
    } else {
      const lastIdx = buckets.length - 1;
      let stride: number;
      if (timeRange === 'Last 1 hour') stride = 2;
      else if (timeRange === 'Last 24 hours') stride = 4;
      else if (timeRange === 'Last 7 days') stride = 3;
      else stride = 5;

      const labelIndices: number[] = [];
      for (let i = 0; i <= lastIdx; i += stride) {
        labelIndices.push(i);
      }
      const isTimeLabel = timeRange === 'Last 1 hour' || timeRange === 'Last 24 hours';
      xLabels = labelIndices.map((i) => ({
        x: Math.max(CHART_LEFT_MARGIN, Math.min(624, buckets[i]!.x)),
        label: isTimeLabel
          ? new Date(buckets[i]!.startMs).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
          : new Date(buckets[i]!.startMs).toLocaleString('en-US', { month: 'short', day: 'numeric' }),
        textAnchor: 'middle',
      }));
    }

    const mid = (yMax + yMin) / 2;
    const formatLabel = (v: number) => (useDecimals ? `${v.toFixed(1)}%` : `${Math.round(v)}%`);
    const gridLines = [yMax, mid, yMin].map((rate) => ({
      y: yScale(rate),
      labelY: yScale(rate) + 4,
      label: formatLabel(rate),
      labelX: CHART_LEFT_MARGIN - 6,
    }));

    const installOverallRates: Record<string, number> = {};
    for (const id of allInstallIds) {
      let totalEvents = 0;
      let totalSuccesses = 0;
      for (const p of nonEmptyPoints) {
        totalEvents += p.installCounts?.[id] ?? 0;
        totalSuccesses += p.installSuccessCounts?.[id] ?? 0;
      }
      if (totalEvents > 0) {
        installOverallRates[id] = (totalSuccesses * 100.0) / totalEvents;
      }
    }

    const legend = allInstallIds.map((id) => ({
      installId: id,
      label: installLabelMap[id] ?? id,
      color: installColorMap[id] ?? '#7083ff',
      isHidden: hidden.has(id),
      rate: installOverallRates[id] != null ? formatSuccessRate(installOverallRates[id]) : null,
      volume: installTotals[id] ?? 0,
    }));

    return { lines, buckets, xLabels, gridLines, legend };
  }

  get _eventTypeChartComputed(): EventTypeChartComputed | null {
    const points = this._rawChartData;
    if (!points.length) return null;

    const installTotals: Record<string, number> = {};
    for (const p of points) {
      for (const [id, count] of Object.entries(p.installCounts ?? {})) {
        installTotals[id] = (installTotals[id] ?? 0) + count;
      }
    }
    const allInstallIds = Object.keys(installTotals).sort((a, b) => (installTotals[b] ?? 0) - (installTotals[a] ?? 0));
    if (!allInstallIds.length) return null;

    const installColorMap: Record<string, string> = {};
    allInstallIds.forEach((id, idx) => {
      installColorMap[id] = CHART_COLORS[idx % CHART_COLORS.length] ?? '#7083ff';
    });

    const installLabelMap: Record<string, string> = {};
    for (const opt of this.args.installOptions) {
      if (opt.value) installLabelMap[opt.value] = opt.label;
    }

    const hidden = new Set(this._hiddenInstalls);
    const visibleInstallIds = allInstallIds.filter((id) => !hidden.has(id));

    const bucketVisibleTotals = points.map((p) => {
      const counts = p.installCounts ?? {};
      return visibleInstallIds.reduce((s, id) => s + (counts[id] ?? 0), 0);
    });
    const maxTotal = Math.max(...bucketVisibleTotals, 1);

    const range = maxTotal;
    let yStep: number;
    if (range <= 10) yStep = 1;
    else if (range <= 50) yStep = 5;
    else if (range <= 100) yStep = 10;
    else if (range <= 500) yStep = 50;
    else yStep = Math.pow(10, Math.floor(Math.log10(range / 3)));
    const yMaxDisplay = Math.ceil(maxTotal / yStep) * yStep;

    const chartBottom = 90;
    const chartH = 80;
    const yScale = (count: number) => Math.round((count / Math.max(yMaxDisplay, 1)) * chartH);

    const slotW = (624 - CHART_LEFT_MARGIN) / points.length;
    const gap = Math.max(1, Math.round(slotW * 0.12));
    const bw = Math.max(1, Math.floor(slotW - gap));

    const bars = points.map((p, i) => {
      const installCounts = p.installCounts ?? {};
      const typeCounts = p.typeCounts ?? {};
      const total = bucketVisibleTotals[i] ?? 0;
      const barX = Math.round(i * slotW) + CHART_LEFT_MARGIN;
      let bottomY = chartBottom;
      const rawSegs: { installId: string; count: number; y: number; height: number; color: string }[] = [];
      for (const id of visibleInstallIds) {
        const count = installCounts[id] ?? 0;
        if (!count) continue;
        const h = yScale(count);
        if (!h) continue;
        const y = bottomY - h;
        rawSegs.push({ installId: id, count, y, height: h, color: installColorMap[id] ?? '#7083ff' });
        bottomY -= h;
      }
      const r = 1;
      const segments = rawSegs.map((seg, si) => {
        const isTop = si === rawSegs.length - 1;
        const x = barX;
        const w = bw;
        const { y, height: h } = seg;
        const pathD = isTop
          ? `M${x + r},${y} L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} L${x + w},${y + h} L${x},${
              y + h
            } L${x},${y + r} Q${x},${y} ${x + r},${y} Z`
          : `M${x},${y} L${x + w},${y} L${x + w},${y + h} L${x},${y + h} Z`;
        return { ...seg, pathD };
      });
      return {
        x: Math.round(i * slotW + slotW / 2) + CHART_LEFT_MARGIN,
        barX,
        barWidth: bw,
        total,
        topY: total > 0 ? chartBottom - yScale(total) : chartBottom,
        segments,
        startMs: p.startMs,
        isEmpty: total === 0,
        typeCounts,
        installCounts,
        installTypeCounts: p.installTypeCounts ?? {},
      };
    });

    const yMid = Math.round(yMaxDisplay / 2);
    const gridLines = [yMaxDisplay, yMid, 0].map((count) => ({
      y: chartBottom - yScale(count),
      labelY: chartBottom - yScale(count) + 4,
      label: count >= 1000 ? `${(count / 1000).toFixed(1).replace(/\.0$/, '')}k` : String(count),
      labelX: CHART_LEFT_MARGIN - 6,
    }));

    const formatType = (t: string) =>
      t
        .replace(/^INTEGRATION_OPERATION_TYPE_/i, '')
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());

    const installTypeBreakdowns: Record<string, Record<string, number>> = {};
    for (const p of points) {
      const itc = p.installTypeCounts ?? {};
      for (const [installId, typMap] of Object.entries(itc)) {
        if (!installTypeBreakdowns[installId]) installTypeBreakdowns[installId] = {};
        for (const [opType, count] of Object.entries(typMap)) {
          installTypeBreakdowns[installId][opType] = (installTypeBreakdowns[installId][opType] ?? 0) + count;
        }
      }
    }

    const legend = allInstallIds.map((id) => {
      const typeBreakdown = installTypeBreakdowns[id] ?? {};
      const tooltipLines = Object.entries(typeBreakdown)
        .sort(([, a], [, b]) => b - a)
        .map(([type, count]) => `${formatType(type)}: ${count.toLocaleString()}`)
        .join('\n');
      return {
        installId: id,
        label: installLabelMap[id] ?? id,
        color: installColorMap[id] ?? '#7083ff',
        isHidden: hidden.has(id),
        volume: (installTotals[id] ?? 0).toLocaleString(),
        tooltipText: `${(installTotals[id] ?? 0).toLocaleString()} events\n${tooltipLines}`,
      };
    });

    return { bars, gridLines, legend };
  }

  get _chartSkeletonPlaceholders(): Array<{ x: number; y: number; width: number; height: number }> {
    const days = this.args.chartProgressTotal;
    if (!days) return [];
    const bucketSizeMs = BUCKET_SIZE_MS[this.args.selectedTimeRange];
    const dayMs = 24 * 60 * 60 * 1000;
    const bucketsPerDay = bucketSizeMs ? Math.max(1, Math.round(dayMs / bucketSizeMs)) : 1;
    const total = days * bucketsPerDay;
    const slotW = (624 - CHART_LEFT_MARGIN) / total;
    const gap = Math.max(1, Math.round(slotW * 0.12));
    const bw = Math.max(1, Math.floor(slotW - gap));
    const heights = [40, 55, 35, 60, 45, 50, 38, 65, 42, 52, 48, 58, 36, 44, 62];
    return Array.from({ length: total }, (_, i) => {
      const h = heights[i % heights.length] ?? 40;
      return { x: Math.round(i * slotW) + CHART_LEFT_MARGIN, y: 90 - h, width: bw, height: h };
    });
  }

  get hasChartData(): boolean {
    return this._chartComputed !== null;
  }

  get chartLines(): ChartLine[] {
    return this._chartComputed?.lines ?? [];
  }

  get chartBuckets(): ChartBucket[] {
    return this._chartComputed?.buckets ?? [];
  }

  get chartHoveredBucketIndex(): number | null {
    return this._hoveredDotIndex;
  }

  get chartGridLines(): ChartGridLine[] {
    return this._chartComputed?.gridLines ?? [];
  }

  get chartXLabels(): ChartXLabel[] {
    return this._chartComputed?.xLabels ?? [];
  }

  get chartInstallLegend(): ChartLegendItem[] {
    return this._chartComputed?.legend ?? [];
  }

  get currentRateDisplay(): string {
    const chart = this._chartComputed;
    if (!chart) return 'N/A';
    const withData = chart.buckets.filter((b) => !b.isEmpty && b.successRate !== null);
    if (!withData.length) return 'N/A';
    const successAvg = withData.reduce((s, b) => s + (b.successRate ?? 0), 0) / withData.length;
    return `${formatSuccessRate(successAvg)}%`;
  }

  get totalEventsDisplay(): string {
    const total = this.args.timelineData?.meta?.total;
    return total != null ? total.toLocaleString() : '–';
  }

  get lastSeenDisplay(): string {
    const latestMs = this.args.timelineData?.summary?.lastSeenAt;
    if (!latestMs) return '–';
    const diffMs = Date.now() - latestMs;
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    return `${diffHr}h ago`;
  }

  get operationsSentDisplay(): string {
    const val = this.args.timelineData?.summary?.operationsSent;
    return val != null ? val.toLocaleString() : '–';
  }

  get operationsReceivedDisplay(): string {
    const val = this.args.timelineData?.summary?.operationsReceived;
    return val != null ? val.toLocaleString() : '–';
  }

  get accessEventsDisplay(): string {
    const val = this.args.timelineData?.summary?.accessEventsReceived;
    return val != null ? val.toLocaleString() : '–';
  }

  get hasEventTypeChartData(): boolean {
    return this._eventTypeChartComputed !== null;
  }

  get eventTypeChartBars(): EventTypeChartBar[] {
    return this._eventTypeChartComputed?.bars ?? [];
  }

  get eventTypeChartGridLines(): ChartGridLine[] {
    return this._eventTypeChartComputed?.gridLines ?? [];
  }

  get eventTypeChartHoveredBarIndex(): number | null {
    return this._hoveredEventTypeBarIndex;
  }

  get chartHoveredPoint(): ChartHoveredPoint | null {
    if (this._hoveredDotIndex === null) return null;
    const buckets = this._chartComputed?.buckets ?? [];
    const bucket = buckets[this._hoveredDotIndex];
    if (!bucket || bucket.isEmpty) return null;

    const d = new Date(bucket.startMs);
    const dateValue = d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    const lines = this._chartComputed?.lines ?? [];
    const legend = this._chartComputed?.legend ?? [];
    const highlightedId = this._highlightedInstallId;
    const nearbyIds = new Set(this._highlightedInstallIds);

    const installRows: { dotStyle: ReturnType<typeof htmlSafe>; label: string; rate: string; rawRate: number }[] = [];
    for (const line of lines) {
      const legendItem = legend.find((l) => l.installId === line.installId);
      if (!legendItem || legendItem.isHidden) continue;
      const pt = line.points.find((p) => p.bucketIdx === this._hoveredDotIndex);
      if (!pt) continue;
      if (nearbyIds.size > 0 && !nearbyIds.has(line.installId)) continue;
      installRows.push({
        dotStyle: htmlSafe(`background: ${line.color}`),
        label: legendItem.label,
        rate: formatSuccessRate(pt.rate),
        rawRate: pt.rate,
      });
    }

    const scaleFactor = this._chartSvgPixelWidth ? this._chartSvgPixelWidth / 624 : 1;
    const screenX = bucket.x * scaleFactor;
    const highlightedLine = highlightedId ? lines.find((l) => l.installId === highlightedId) : null;
    const highlightedPt = highlightedLine?.points.find((p) => p.bucketIdx === this._hoveredDotIndex);
    const tooltipY = highlightedPt ? highlightedPt.y * scaleFactor : 30 * scaleFactor;
    const estTooltipH = (installRows.length + 2) * 22 + 16;
    const showBelow = tooltipY < estTooltipH;
    const tooltipStyle = htmlSafe(
      showBelow
        ? `left: ${screenX}px; top: ${tooltipY}px; transform: translateX(-50%) translateY(8px);`
        : `left: ${screenX}px; top: ${tooltipY}px; transform: translateX(-50%) translateY(calc(-100% - 6px));`,
    );

    return {
      x: bucket.x,
      tooltipStyle,
      tooltipBelow: showBelow,
      dateValue,
      successRateDisplay:
        installRows.length === 1
          ? `${installRows[0]!.rate}%`
          : bucket.successRate != null
            ? `${formatSuccessRate(bucket.successRate)}%`
            : '–',
      installRows,
    };
  }

  get eventTypeChartHoveredPoint(): EventTypeChartHoveredPoint | null {
    if (this._hoveredEventTypeBarIndex === null) return null;
    const bars = this._eventTypeChartComputed?.bars ?? [];
    const bar = bars[this._hoveredEventTypeBarIndex];
    if (!bar || bar.isEmpty) return null;

    const d = new Date(bar.startMs);
    const dateValue = d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    const scaleFactor = this._eventTypeChartSvgPixelWidth ? this._eventTypeChartSvgPixelWidth / 624 : 1;
    const tooltipStyle = htmlSafe(
      `left: ${bar.x * scaleFactor}px; top: ${
        bar.topY * scaleFactor
      }px; transform: translateX(-50%) translateY(calc(-100% - 6px));`,
    );

    const formatType = (t: string) =>
      t
        .replace(/^INTEGRATION_OPERATION_TYPE_/i, '')
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());

    const hoveredInstallId = this._hoveredSegmentInstallId;
    if (hoveredInstallId) {
      const legend = this._eventTypeChartComputed?.legend ?? [];
      const legendItem = legend.find((l) => l.installId === hoveredInstallId);
      const installTypeCounts = bar.installTypeCounts?.[hoveredInstallId] ?? {};
      const typeRows = Object.entries(installTypeCounts)
        .filter(([, v]) => v > 0)
        .sort(([, a], [, b]) => b - a)
        .map(([type, count]) => ({ label: formatType(type), count }));

      return {
        x: bar.x,
        tooltipStyle,
        dateValue,
        total: bar.installCounts?.[hoveredInstallId] ?? 0,
        typeRows,
        installLabel: legendItem?.label ?? hoveredInstallId,
        installDotStyle: htmlSafe(`background: ${legendItem?.color ?? '#999'}`),
      };
    }

    const hidden = new Set(this._hiddenInstalls);
    const visibleTypeCounts: Record<string, number> = {};
    for (const [installId, types] of Object.entries(bar.installTypeCounts ?? {})) {
      if (hidden.has(installId)) continue;
      for (const [type, count] of Object.entries(types)) {
        visibleTypeCounts[type] = (visibleTypeCounts[type] ?? 0) + count;
      }
    }
    const effectiveCounts = Object.keys(visibleTypeCounts).length > 0 ? visibleTypeCounts : bar.typeCounts;
    const typeRows = Object.entries(effectiveCounts)
      .filter(([, v]) => v > 0)
      .sort(([, a], [, b]) => b - a)
      .map(([type, count]) => ({ label: formatType(type), count }));

    return { x: bar.x, tooltipStyle, dateValue, total: bar.total, typeRows, installLabel: null, installDotStyle: null };
  }

  @action toggleInstallSeries(installId: string): void {
    if (this._hiddenInstalls.includes(installId)) {
      this._hiddenInstalls = this._hiddenInstalls.filter((id) => id !== installId);
    } else {
      const newHidden = [...this._hiddenInstalls, installId];
      const legendIds = new Set(this.chartInstallLegend.map((item) => item.installId));
      const relevantHidden = newHidden.filter((id) => legendIds.has(id));
      if (relevantHidden.length >= legendIds.size) return;
      this._hiddenInstalls = newHidden;
    }
  }

  @action highlightInstall(installId: string): void {
    this._highlightedInstallId = installId;
  }

  @action clearHighlight(): void {
    this._highlightedInstallId = null;
  }

  @action onChartMouseMove(event: MouseEvent): void {
    const svg = event.currentTarget as SVGSVGElement;
    const rect = svg.getBoundingClientRect();
    this._chartSvgPixelWidth = rect.width;
    const mouseX = ((event.clientX - rect.left) / rect.width) * 624;
    const mouseY = ((event.clientY - rect.top) / rect.height) * 118;
    const buckets = this._chartComputed?.buckets ?? [];
    if (!buckets.length) return;

    let closest = -1;
    let minDist = Infinity;
    for (let i = 0; i < buckets.length; i++) {
      if (buckets[i]!.isEmpty) continue;
      const dist = Math.abs(buckets[i]!.x - mouseX);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    }
    this._hoveredDotIndex = closest === -1 ? null : closest;

    if (closest >= 0) {
      const lines = this._chartComputed?.lines ?? [];
      const legend = this._chartComputed?.legend ?? [];
      const lineDistances: { installId: string; dist: number }[] = [];
      for (const line of lines) {
        const legendItem = legend.find((l) => l.installId === line.installId);
        if (!legendItem || legendItem.isHidden) continue;
        const pt = line.points.find((p) => p.bucketIdx === closest);
        if (!pt) continue;
        lineDistances.push({ installId: line.installId, dist: Math.abs(pt.y - mouseY) });
      }
      lineDistances.sort((a, b) => a.dist - b.dist);
      const nearest = lineDistances[0];
      if (nearest) {
        const nearbyIds = lineDistances.filter((l) => l.dist <= nearest.dist + 2).map((l) => l.installId);
        this._highlightedInstallId = nearest.installId;
        this._highlightedInstallIds = nearbyIds;
      } else {
        this._highlightedInstallId = null;
        this._highlightedInstallIds = [];
      }
    } else {
      this._highlightedInstallId = null;
    }
  }

  @action onChartLeave(): void {
    this._hoveredDotIndex = null;
    this._highlightedInstallId = null;
    this._highlightedInstallIds = [];
  }

  @action onChartClick(): void {
    if (!this._highlightedInstallId) return;
    const id = this._highlightedInstallId;
    const legend = this.chartInstallLegend;
    const allVisible = legend.filter((l) => !l.isHidden);
    if (allVisible.length === 1 && allVisible[0]!.installId === id) {
      this._hiddenInstalls = [];
    } else {
      this._hiddenInstalls = legend.filter((l) => l.installId !== id).map((l) => l.installId);
    }
  }

  @action onEventTypeChartMouseMove(event: MouseEvent): void {
    const svg = event.currentTarget as SVGSVGElement;
    const rect = svg.getBoundingClientRect();
    this._eventTypeChartSvgPixelWidth = rect.width;
    const mouseX = ((event.clientX - rect.left) / rect.width) * 624;
    const mouseY = ((event.clientY - rect.top) / rect.height) * 118;
    const bars = this._eventTypeChartComputed?.bars ?? [];
    if (!bars.length) return;

    let closest = -1;
    let minDist = Infinity;
    for (let i = 0; i < bars.length; i++) {
      if (bars[i]!.isEmpty) continue;
      const dist = Math.abs(bars[i]!.x - mouseX);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    }
    this._hoveredEventTypeBarIndex = closest === -1 ? null : closest;

    let hoveredInstall: string | null = null;
    if (closest >= 0) {
      const bar = bars[closest]!;
      for (const seg of bar.segments) {
        if (mouseY >= seg.y && mouseY <= seg.y + seg.height) {
          hoveredInstall = seg.installId;
          break;
        }
      }
    }
    this._hoveredSegmentInstallId = hoveredInstall;
  }

  @action onEventTypeChartLeave(): void {
    this._hoveredEventTypeBarIndex = null;
    this._hoveredSegmentInstallId = null;
  }

  @action onEventTypeChartClick(): void {
    const hoveredId = this._hoveredSegmentInstallId;
    if (!hoveredId) return;
    const legend = this.chartInstallLegend;
    const allVisible = legend.filter((l) => !l.isHidden);
    if (allVisible.length === 1 && allVisible[0]!.installId === hoveredId) {
      this._hiddenInstalls = [];
    } else {
      this._hiddenInstalls = legend.filter((l) => l.installId !== hoveredId).map((l) => l.installId);
    }
  }

  <template>
    {{! Charts card — success line chart + events stacked bar chart + shared legend }}
    <UiCard class="charts-with-legend mb-3 p-0" style="overflow: visible">
      <div class="charts-column">
        {{! Success chart }}
        <div class="p-5" style="overflow: visible">
          <div class="flex items-center gap-2 mb-2">
            <span class="text-sm font-semibold text-carbon-70">Success</span>
            <span class="text-sm font-bold text-carbon-70">{{this.currentRateDisplay}}</span>
            <span class="text-xs text-carbon-40">in {{this.selectedTimeRangeLabel}}</span>
            <span class="ml-auto cursor-default shrink-0">
              <img src="/assets/images/gray-info.svg" alt="info" class="w-3.5 h-3.5" />
              <EmberTooltip @side="top" @text="Success rate across integration installs" @popperContainer=".ui__page" />
            </span>
          </div>
          <div class="relative" style="overflow: visible">
            {{#if @isLoading}}
              <div class="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center z-10">
                <LoadingSpinnerPulse />
              </div>
            {{/if}}
            {{#if this.hasChartData}}
              <svg
                viewBox="0 0 624 118"
                class="block w-full cursor-pointer overflow-visible"
                style="overflow: visible"
                {{on "mousemove" this.onChartMouseMove}}
                {{on "mouseleave" this.onChartLeave}}
                {{on "click" this.onChartClick}}
              >
                {{#each this.chartGridLines as |gl|}}
                  <line x1="55" y1={{gl.y}} x2="624" y2={{gl.y}} stroke="#e5e7eb" stroke-width="0.5" />
                  <text
                    x={{gl.labelX}}
                    y={{gl.labelY}}
                    text-anchor="end"
                    font-size="12"
                    fill="#6b7280"
                  >{{gl.label}}</text>
                {{/each}}
                {{#each this.chartLines as |line|}}
                  {{#if line.pathD}}
                    {{#if (not (and this._highlightedInstallId (eq line.installId this._highlightedInstallId)))}}
                      <path
                        d={{line.pathD}}
                        fill="none"
                        stroke={{if this._highlightedInstallId "#d1d5db" line.color}}
                        stroke-width="1.5"
                        stroke-linejoin="round"
                        stroke-linecap="round"
                      />
                      {{#unless this._highlightedInstallId}}
                        {{#each line.points as |pt|}}
                          <circle
                            cx={{pt.x}}
                            cy={{pt.y}}
                            r={{if (eq pt.bucketIdx this.chartHoveredBucketIndex) "3.5" "2"}}
                            fill={{line.color}}
                          />
                        {{/each}}
                      {{/unless}}
                    {{/if}}
                  {{/if}}
                {{/each}}
                {{#if this._highlightedInstallId}}
                  {{#each this.chartLines as |line|}}
                    {{#if (and line.pathD (eq line.installId this._highlightedInstallId))}}
                      <path
                        d={{line.pathD}}
                        fill="none"
                        stroke={{line.color}}
                        stroke-width="3"
                        stroke-linejoin="round"
                        stroke-linecap="round"
                      />
                      {{#each line.points as |pt|}}
                        <circle
                          cx={{pt.x}}
                          cy={{pt.y}}
                          r={{if (eq pt.bucketIdx this.chartHoveredBucketIndex) "4" "2.5"}}
                          fill={{line.color}}
                        />
                      {{/each}}
                    {{/if}}
                  {{/each}}
                {{/if}}
                {{#each this.chartXLabels as |xl|}}
                  <text
                    x={{xl.x}}
                    y="114"
                    text-anchor={{xl.textAnchor}}
                    font-size="12"
                    fill="#6b7280"
                  >{{xl.label}}</text>
                {{/each}}
                {{#if this.chartHoveredPoint}}
                  <line
                    x1={{this.chartHoveredPoint.x}}
                    y1="10"
                    x2={{this.chartHoveredPoint.x}}
                    y2="90"
                    stroke="#9ca3af"
                    stroke-width="1"
                    stroke-dasharray="3,2"
                  />
                {{/if}}
              </svg>
              {{#if this.chartHoveredPoint}}
                <div class="absolute z-50 pointer-events-none" style={{this.chartHoveredPoint.tooltipStyle}}>
                  <div
                    class="relative rounded-lg text-white text-sm whitespace-nowrap"
                    style="background: #3a3c47; padding: 6px 10px; text-shadow: -1px -1px 0 rgba(0,0,0,0.2);"
                  >
                    {{#if this.chartHoveredPoint.tooltipBelow}}
                      <div
                        class="absolute"
                        style="top: -5px; left: calc(50% - 5px); width: 0; height: 0; border: 5px solid transparent; border-top-width: 0; border-bottom-color: #3a3c47;"
                      ></div>
                    {{/if}}
                    <div class="flex justify-between gap-6">
                      <span class="opacity-75">Date</span>
                      <span>{{this.chartHoveredPoint.dateValue}}</span>
                    </div>
                    <div class="flex justify-between gap-6 mt-1">
                      <span class="opacity-75">Success</span>
                      <span>{{this.chartHoveredPoint.successRateDisplay}}</span>
                    </div>
                    {{#each this.chartHoveredPoint.installRows as |row|}}
                      <div class="flex justify-between gap-6 mt-0.5">
                        <span class="flex items-center gap-1.5">
                          <span class="w-2 h-2 rounded-sm shrink-0 inline-block" style={{row.dotStyle}}></span>
                          <span class="opacity-75">{{row.label}}</span>
                        </span>
                        <span>{{row.rate}}%</span>
                      </div>
                    {{/each}}
                    {{#unless this.chartHoveredPoint.tooltipBelow}}
                      <div
                        class="absolute"
                        style="bottom: -5px; left: calc(50% - 5px); width: 0; height: 0; border: 5px solid transparent; border-bottom-width: 0; border-top-color: #3a3c47;"
                      ></div>
                    {{/unless}}
                  </div>
                </div>
              {{/if}}
            {{else}}
              <div class="flex items-center justify-center h-20 text-xs text-carbon-30">No data</div>
            {{/if}}
          </div>
        </div>

        <div class="border-t border-gray-200"></div>

        {{! Events chart }}
        <div class="p-5" style="overflow: visible">
          <div class="flex items-center gap-2 mb-1">
            <span class="text-sm font-semibold text-carbon-70">Events</span>
            <span class="text-sm font-bold text-carbon-70">{{this.totalEventsDisplay}}</span>
            <span class="text-xs text-carbon-40">in {{this.selectedTimeRangeLabel}}</span>
            <span class="ml-auto cursor-default shrink-0">
              <img src="/assets/images/gray-info.svg" alt="info" class="w-3.5 h-3.5" />
              <EmberTooltip @side="top" @text="Event counts by integration install" @popperContainer=".ui__page" />
            </span>
          </div>
          <div class="flex items-center gap-3 mb-2">
            <span class="text-xs text-carbon-40">Last seen:
              <span class="font-semibold text-carbon-70">{{this.lastSeenDisplay}}</span></span>
            {{#if @isChartProgressing}}
              <span class="text-xs text-carbon-30">Loading {{@chartProgressLoaded}}/{{@chartProgressTotal}} days…</span>
            {{/if}}
          </div>
          <div class="relative" style="overflow: visible">
            {{#if @isLoading}}
              <div class="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center z-10">
                <LoadingSpinnerPulse />
              </div>
            {{/if}}
            {{#if this.hasEventTypeChartData}}
              <svg
                viewBox="0 0 624 118"
                class="block w-full cursor-pointer overflow-visible"
                style="overflow: visible"
                {{on "mousemove" this.onEventTypeChartMouseMove}}
                {{on "mouseleave" this.onEventTypeChartLeave}}
                {{on "click" this.onEventTypeChartClick}}
              >
                {{#each this.eventTypeChartGridLines as |gl|}}
                  <line x1="55" y1={{gl.y}} x2="624" y2={{gl.y}} stroke="#e5e7eb" stroke-width="0.5" />
                  <text
                    x={{gl.labelX}}
                    y={{gl.labelY}}
                    text-anchor="end"
                    font-size="12"
                    fill="#6b7280"
                  >{{gl.label}}</text>
                {{/each}}
                <g>
                  {{#each this.eventTypeChartBars key="startMs" as |bar i|}}
                    <g {{restartAnimation @chartLoadKey}}>
                      {{#each bar.segments as |seg|}}
                        <path
                          d={{seg.pathD}}
                          fill={{seg.color}}
                          fill-opacity={{if
                            this._highlightedInstallId
                            (if (eq seg.installId this._highlightedInstallId) "1" "0.15")
                            (if (eq i this.eventTypeChartHoveredBarIndex) "1" "0.7")
                          }}
                        />
                      {{/each}}
                    </g>
                  {{/each}}
                </g>
                {{#each this.chartXLabels as |xl|}}
                  <text
                    x={{xl.x}}
                    y="114"
                    text-anchor={{xl.textAnchor}}
                    font-size="12"
                    fill="#6b7280"
                  >{{xl.label}}</text>
                {{/each}}
                {{#if this.eventTypeChartHoveredPoint}}
                  <line
                    x1={{this.eventTypeChartHoveredPoint.x}}
                    y1="10"
                    x2={{this.eventTypeChartHoveredPoint.x}}
                    y2="90"
                    stroke="#9ca3af"
                    stroke-width="1"
                    stroke-dasharray="3,2"
                  />
                {{/if}}
              </svg>
              {{#if this.eventTypeChartHoveredPoint}}
                <div class="absolute z-50 pointer-events-none" style={{this.eventTypeChartHoveredPoint.tooltipStyle}}>
                  <div
                    class="relative rounded-lg text-white text-sm whitespace-nowrap"
                    style="background: #3a3c47; padding: 6px 10px; text-shadow: -1px -1px 0 rgba(0,0,0,0.2);"
                  >
                    {{#if this.eventTypeChartHoveredPoint.installLabel}}
                      <div class="flex items-center gap-1.5 mb-1">
                        <span
                          class="w-2 h-2 rounded-sm shrink-0 inline-block"
                          style={{this.eventTypeChartHoveredPoint.installDotStyle}}
                        ></span>
                        <span class="font-medium">{{this.eventTypeChartHoveredPoint.installLabel}}</span>
                      </div>
                      <div class="flex justify-between gap-6">
                        <span class="opacity-75 text-xs">{{this.eventTypeChartHoveredPoint.dateValue}}</span>
                      </div>
                      {{#each this.eventTypeChartHoveredPoint.typeRows as |row|}}
                        <div class="flex justify-between gap-6 mt-0.5">
                          <span class="opacity-60 text-xs">{{row.label}}</span>
                          <span class="text-xs">{{row.count}}</span>
                        </div>
                      {{/each}}
                    {{else}}
                      <div class="flex justify-between gap-6">
                        <span class="opacity-75">Date</span>
                        <span>{{this.eventTypeChartHoveredPoint.dateValue}}</span>
                      </div>
                      <div class="flex justify-between gap-6 mt-1">
                        <span class="opacity-75">Total</span>
                        <span>{{this.eventTypeChartHoveredPoint.total}}</span>
                      </div>
                      {{#each this.eventTypeChartHoveredPoint.typeRows as |row|}}
                        <div class="flex justify-between gap-6 mt-0.5">
                          <span class="opacity-60 text-xs">{{row.label}}</span>
                          <span class="text-xs">{{row.count}}</span>
                        </div>
                      {{/each}}
                    {{/if}}
                    <div
                      class="absolute"
                      style="bottom: -5px; left: calc(50% - 5px); width: 0; height: 0; border: 5px solid transparent; border-bottom-width: 0; border-top-color: #3a3c47;"
                    ></div>
                  </div>
                </div>
              {{/if}}
            {{else if @isChartProgressing}}
              <svg viewBox="0 0 624 118" class="block w-full">
                {{#each this._chartSkeletonPlaceholders as |bar|}}
                  <rect
                    x={{bar.x}}
                    y={{bar.y}}
                    width={{bar.width}}
                    height={{bar.height}}
                    fill="#e5e7eb"
                    rx="1"
                    class="chart-skeleton-bar"
                  />
                {{/each}}
              </svg>
            {{else}}
              <div class="flex items-center justify-center h-20 text-xs text-carbon-30">No data</div>
            {{/if}}
          </div>
        </div>
      </div>

      {{! Shared legend }}
      <div class="charts-legend p-3">
        <div class="charts-legend-inner">
          {{#each this.chartInstallLegend as |item|}}
            <button
              type="button"
              class="flex items-start gap-1.5 rounded px-1 py-1 -mx-1 hover:bg-carbon-5 w-full"
              {{on "click" (fn this.toggleInstallSeries item.installId)}}
              {{on "mouseenter" (fn this.highlightInstall item.installId)}}
              {{on "mouseleave" this.clearHighlight}}
            >
              <svg
                width="8"
                height="8"
                viewBox="0 0 8 8"
                class="shrink-0 mt-0.5"
                style={{if item.isHidden "opacity: 0.4"}}
              >
                <rect width="8" height="8" rx="1" fill={{item.color}} />
              </svg>
              <span
                class="text-xs text-left leading-tight whitespace-nowrap
                  {{if item.isHidden 'text-carbon-30 line-through' 'text-carbon-50'}}"
              >{{item.label}}</span>
              {{#unless item.isHidden}}
                {{#if item.rate}}
                  <EmberTooltip @side="left" @text="Success rate: {{item.rate}}%" @popperContainer=".ui__page" />
                {{/if}}
              {{/unless}}
            </button>
          {{/each}}
        </div>
      </div>
    </UiCard>

    {{! Total operations summary }}
    <div class="mb-2 py-1">
      <div class="text-sm font-medium text-carbon-70">
        <span class="font-bold">{{this.totalEventsDisplay}}</span>
        events in
        {{this.selectedTimeRangeLabel}}
      </div>
      <div class="flex flex-col gap-0.5 mt-1 pl-4">
        <div class="flex items-center gap-1 text-xs text-carbon-40">
          <span>{{this.operationsSentDisplay}} operations sent</span>
          <span class="cursor-default">
            <img src="/assets/images/gray-info.svg" alt="info" class="w-3 h-3" />
            <EmberTooltip
              @side="top"
              @text="Total operations sent from Envoy to the integration"
              @popperContainer=".ui__page"
            />
          </span>
        </div>
        <div class="flex items-center gap-1 text-xs text-carbon-40">
          <span>{{this.operationsReceivedDisplay}} operations received</span>
          <span class="cursor-default">
            <img src="/assets/images/gray-info.svg" alt="info" class="w-3 h-3" />
            <EmberTooltip
              @side="top"
              @text="Total operations received by Envoy from the integration"
              @popperContainer=".ui__page"
            />
          </span>
        </div>
        <div class="flex items-center gap-1 text-xs text-carbon-40">
          <span>{{this.accessEventsDisplay}} access events</span>
          <span class="cursor-default">
            <img src="/assets/images/gray-info.svg" alt="info" class="w-3 h-3" />
            <EmberTooltip
              @side="top"
              @text="Total access events received during this period"
              @popperContainer=".ui__page"
            />
          </span>
        </div>
      </div>
    </div>
  </template>
}
